import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button, Checkbox, Field, Input, MessageBar, MessageBarBody } from "@fluentui/react-components";
import { RegisterTrainerInput } from "@gymplan/shared";
import { AuthCard } from "../components/AuthCard.tsx";
import { useAuth } from "../lib/auth.tsx";
import { emit } from "../lib/analytics.ts";

/**
 * Trainer sign-up, step 1 of 3: who you are and where you train — nothing else.
 * How you work (session length, days, hours, planning day) is asked in the setup chat next.
 */
export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    acceptTerms: false,
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const openedAt = useRef(Date.now());
  useEffect(() => emit({ event: "signup_started" }), []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const parsed = RegisterTrainerInput.safeParse({
      ...f,
      signupDurationS: Math.min(86_400, Math.round((Date.now() - openedAt.current) / 1000)),
    });
    if (!parsed.success) {
      setErr(parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join(" · "));
      return;
    }
    setBusy(true);
    try {
      await register(parsed.data);
      nav("/setup", { replace: true });
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Create your trainer account" subtitle="Step 1 of 3 · Then a few quick questions about where and when you train." wide>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {err && (
          <MessageBar intent="error">
            <MessageBarBody>{err}</MessageBarBody>
          </MessageBar>
        )}
        <Field label="Your name" required>
          <Input autoComplete="name" value={f.name} onChange={(_, d) => setF({ ...f, name: d.value })} />
        </Field>
        <Field label="WhatsApp number" required hint="International format, e.g. +44 7700 900123 — check-in alerts come here">
          <Input type="tel" autoComplete="tel" value={f.phone} onChange={(_, d) => setF({ ...f, phone: d.value.replace(/\s+/g, "") })} />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, alignItems: "start" }}>
          <Field label="Email" required>
            <Input type="email" autoComplete="email" value={f.email} onChange={(_, d) => setF({ ...f, email: d.value })} />
          </Field>
          <Field label="Password" required hint="At least 10 characters">
            <Input type="password" autoComplete="new-password" value={f.password} onChange={(_, d) => setF({ ...f, password: d.value })} />
          </Field>
        </div>
        <Checkbox
          checked={f.acceptTerms}
          onChange={(_, d) => setF({ ...f, acceptTerms: !!d.checked })}
          label={
            <>
              I accept the <Link to="/privacy/terms" target="_blank">terms and data processing terms</Link> and have read the <Link to="/privacy/trainers" target="_blank">privacy notice</Link>. I'll only add clients who've agreed to be messaged and are 18 or over.
            </>
          }
        />
        <Button appearance="primary" type="submit" disabled={busy || !f.acceptTerms} size="large">
          Create account
        </Button>
        <span>
          Already have an account? <Link to="/login">Sign in</Link>
        </span>
      </form>
    </AuthCard>
  );
}
